import { useState, useEffect } from "react";
import { useMemo } from "react";
import apiPath from "../constants/apiPath";
import Http from "../services/Http";
import toast from "react-hot-toast";
import Currency from "../view/Component/Currency";
import Header from "../layout/Header";
import Footer from "../layout/Footer";


const formatPrice = (p) => {
    const s = p.toFixed(3);
    return s;
};

const Stars = ({ rating, size = 12 }) => {
    if (!rating) return null;
    return (
        <span style={{ display: "inline-flex", gap: 1 }}>
            {[...Array(5)].map((_, i) => (
                <svg key={i} width={size} height={size} viewBox="0 0 20 20">
                    <polygon points="10,1.5 12.2,7 18,7.5 13.5,11.5 15,17.5 10,14.5 5,17.5 6.5,11.5 2,7.5 7.8,7"
                        fill={i < Math.floor(rating) ? "#E8B231" : "#DDD"}
                        opacity={i < Math.floor(rating) ? 1 : i === Math.floor(rating) && rating % 1 > 0 ? rating % 1 : 0.2} />
                </svg>
            ))}
        </span>
    );
};

export default function TrySamples() {
    const [search, setSearch] = useState("");
    const [sel, setSel] = useState(null);
    const [modal, setModal] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [filter, setFilter] = useState("All");
    const [sort, setSort] = useState("popular");
    const [ready, setReady] = useState(false);

    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);

    const normalize = (item) => ({
        id: item.id,
        product_id: item.product_id || item.parent_id || null, // full product
        attribute_id: item.attribute_id || null,

        name: item.name || "",
        brand: item.brand || "",
        image: item.image || "",

        type: item.type || "",
        fullSize: item.fullSize || "",
        fullPrice: item.fullPrice || 0,
        originalFullPrice: item.originalFullPrice || 0,
        discount: item.discount || 0,

        sold: item.sold || 0,
        left: item.left || 0,

        samplePrice: item.samplePrice || 0,
        sampleSize: item.sampleSize || "",

        rating: item.rating || 0,
        reviews: item.reviews || 0,

        gender: item.gender || "Unisex",
        notes: item.notes || []
    });

    const getProducts = async () => {
        try {
            setLoading(true);

            const result = await Http(apiPath.productSample, {
                method: "POST"
            });

            if (result.status === 200 && result.data?.data) {
                const raw = result.data.data;
                const mapped = raw.map(normalize);

                setProducts(mapped);
            } else {
                toast.error(result.data?.message || "Load failed");
            }

        } catch (e) {
            console.error(e);
            toast.error("Load failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getProducts();
    }, []);

    /**
     * Add to cart (sample vs full)
     */
    const addToCart = async (product, type = "sample") => {
        try {
            const data = new FormData();

            data.append("qty", 1);

            if (type === "sample") {
                data.append("product_id", product.id);
            } else {
                if (!product.product_id || !product.attribute_id) {
                    toast.error("Product variant not available");
                    return false;
                }

                data.append("product_id", product.product_id);
                data.append("product_attribute_values_id", product.attribute_id);
            }

            const result = await Http(apiPath.AddtoCart, {
                method: "POST",
                data
            });

            if (result.status === 200 && result.data?.status) {
                toast.success(result.data.message || "Added to cart");
                return true;
            } else {
                toast.error(result.data?.message || "Add failed");
                return false;
            }

        } catch (e) {
            console.error(e);
            toast.error("Add to cart failed");
            return false;
        }
    };

    useEffect(() => { setReady(true); }, []);

    const filtered = useMemo(() => {
        return products.filter(p => {
            const s = search.toLowerCase();

            const ms =
                !search ||
                p.name?.toLowerCase().includes(s) ||
                p.brand?.toLowerCase().includes(s) ||
                (p.notes || []).some(n => n.toLowerCase().includes(s));

            const mf = filter === "All" || p.gender === filter;

            return ms && mf;
        })
            .sort((a, b) => {
                if (sort === "popular") return (b.sold || 0) - (a.sold || 0);
                if (sort === "price-low") return (a.samplePrice || 0) - (b.samplePrice || 0);
                if (sort === "price-high") return (b.samplePrice || 0) - (a.samplePrice || 0);
                if (sort === "rating") return (b.rating || 0) - (a.rating || 0);
                return 0;
            });
    }, [products, search, filter, sort]);

    const open = (p) => { setSel(p); setModal(true); setFeedback(null); };
    const close = () => { setModal(false); setTimeout(() => setSel(null), 280); };
    const add = async (type) => {
        const ok = await addToCart(sel, type);

        if (!ok) return;

        setFeedback(type);

        setTimeout(() => {
            close();
            setFeedback(null);
        }, 1200);
    };

    return (
        <>
            <Header />
            <div style={{ minHeight: "100vh", background: "#FDFBF8", fontFamily: "'Playfair Display', Georgia, serif" }}>
                <style>{`
            @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;1,400;1,500&family=DM+Sans:wght@300;400;500;600;700&display=swap');
            *{box-sizing:border-box;margin:0;padding:0}
            :root{--bk:#2C1810;--cm:#C49A6C;--sd:#E8DDD0;--cr:#FDFBF8;--rs:#C8485B;--sg:#4A8A2A;--bd:'DM Sans',system-ui,sans-serif;--pk:#E91E8C}

            /* ── HERO ── */
            .hero{background:linear-gradient(175deg,#F3ECE0 0%,#FDFBF8 55%,#FFFAF3 100%);padding:40px 20px 32px;text-align:center;position:relative;overflow:hidden}
            .hero::before{content:'';position:absolute;top:-100px;right:-60px;width:300px;height:300px;border-radius:50%;background:radial-gradient(circle,rgba(233,30,140,0.05) 0%,transparent 65%)}
            .hero::after{content:'';position:absolute;bottom:-50px;left:-30px;width:200px;height:200px;border-radius:50%;background:radial-gradient(circle,rgba(196,154,108,0.1) 0%,transparent 70%)}

            .h-pill{display:inline-flex;align-items:center;gap:8px;background:var(--pk);color:white;padding:7px 24px;border-radius:100px;font-family:var(--bd);font-size:10px;font-weight:700;letter-spacing:3px;text-transform:uppercase;margin-bottom:16px;animation:rv .5s ease-out both}
            .h-t{font-size:clamp(26px,5vw,46px);font-weight:500;color:var(--bk);line-height:1.15;margin-bottom:8px;letter-spacing:-.3px;animation:rv .5s .07s ease-out both}
            .h-t em{font-style:italic;color:var(--pk);font-weight:600}
            .h-sub{font-family:var(--bd);font-size:14px;color:#8B7B6B;max-width:480px;margin:0 auto 20px;line-height:1.65;animation:rv .5s .13s ease-out both}

            .h-how{display:flex;justify-content:center;gap:8px;flex-wrap:wrap;margin-bottom:24px;animation:rv .5s .18s ease-out both}
            .h-step{display:flex;align-items:center;gap:10px;background:white;border:1px solid rgba(233,30,140,0.1);border-radius:14px;padding:10px 18px;font-family:var(--bd);font-size:12px;color:#5A4A3A;font-weight:500}
            .h-sn{width:26px;height:26px;border-radius:50%;background:var(--pk);color:white;display:flex;align-items:center;justify-content:center;font-size:11px;font-weight:700;flex-shrink:0}

            .h-bar{max-width:680px;margin:0 auto;display:flex;gap:8px;flex-wrap:wrap;animation:rv .5s .23s ease-out both}
            .h-sbox{flex:1;min-width:200px;position:relative}
            .h-inp{width:100%;padding:12px 16px 12px 42px;border:1.5px solid rgba(233,30,140,0.15);border-radius:12px;background:white;font-family:var(--bd);font-size:13px;color:var(--bk);outline:none;transition:all .25s}
            .h-inp:focus{border-color:var(--pk);box-shadow:0 4px 16px rgba(233,30,140,0.08)}
            .h-inp::placeholder{color:#C0B0A0}
            .h-si{position:absolute;left:14px;top:50%;transform:translateY(-50%);color:#C0B0A0}
            .h-fl{display:flex;gap:4px}
            .h-fb{padding:8px 14px;border:1.5px solid rgba(233,30,140,0.12);border-radius:10px;background:white;font-family:var(--bd);font-size:11px;font-weight:600;color:#8B7B6B;cursor:pointer;transition:all .2s;white-space:nowrap}
            .h-fb:hover{border-color:var(--pk);color:var(--pk)}
            .h-fb.on{background:var(--pk);color:white;border-color:var(--pk)}
            .h-sort{padding:8px 12px;border:1.5px solid rgba(233,30,140,0.12);border-radius:10px;background:white;font-family:var(--bd);font-size:11px;color:#8B7B6B;cursor:pointer;outline:none;font-weight:500}

            /* ── GRID ── */
            .gw{max-width:1140px;margin:0 auto;padding:24px 20px 60px}
            .gc{font-family:var(--bd);font-size:12px;color:#A09080;margin-bottom:16px;display:flex;align-items:center;justify-content:space-between}
            .gg{display:grid;grid-template-columns:repeat(auto-fill,minmax(265px,1fr));gap:16px}

            /* ── CARD ── */
            .cd{background:white;border-radius:18px;border:1px solid rgba(233,30,140,0.06);overflow:hidden;cursor:pointer;transition:all .32s cubic-bezier(.22,.68,0,1.04);position:relative}
            .cd:hover{transform:translateY(-4px);box-shadow:0 14px 36px rgba(44,24,16,0.07);border-color:rgba(233,30,140,0.15)}
            .cd-top{position:relative;display:flex;justify-content:center;align-items:center;padding:18px 16px 10px;background:linear-gradient(180deg,rgba(243,236,224,0.35) 0%,transparent 100%)}
            .cd-badge{position:absolute;top:10px;left:10px;background:var(--pk);color:white;font-family:var(--bd);font-size:9px;font-weight:700;letter-spacing:1px;text-transform:uppercase;padding:4px 10px;border-radius:8px}
            .cd-sale{position:absolute;top:10px;right:10px;background:rgba(233,30,140,0.08);color:var(--pk);font-family:var(--bd);font-size:10px;font-weight:700;padding:3px 8px;border-radius:6px}
            .cd-body{padding:4px 16px 16px}
            .cd-brand{font-family:var(--bd);font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#A09080;margin-bottom:2px}
            .cd-name{font-size:17px;font-weight:600;color:var(--bk);line-height:1.2;margin-bottom:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
            .cd-type{font-family:var(--bd);font-size:10px;color:#B8A898;margin-bottom:6px}
            .cd-rat{display:flex;align-items:center;gap:5px;margin-bottom:6px}
            .cd-rt{font-family:var(--bd);font-size:10px;color:#A09080}
            .cd-notes{display:flex;gap:3px;flex-wrap:wrap;margin-bottom:12px}
            .cd-note{font-family:var(--bd);font-size:9px;font-weight:600;padding:2px 8px;border-radius:6px;background:rgba(233,30,140,0.04);color:#8B7B6B}
            .cd-stock{font-family:var(--bd);font-size:10px;color:#A09080;margin-bottom:10px}
            .cd-stock b{color:var(--rs)}

            /* PRICE COMPARISON */
            .cd-prices{display:flex;border-radius:12px;overflow:hidden;border:1px solid rgba(0,0,0,0.06);margin-bottom:12px}
            .cd-pf{flex:1;padding:9px 10px;background:rgba(200,72,91,0.03);text-align:center;border-right:1px solid rgba(0,0,0,0.04)}
            .cd-ps{flex:1;padding:9px 10px;background:rgba(74,138,42,0.04);text-align:center}
            .cd-pl{font-family:var(--bd);font-size:7.5px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:2px}
            .cd-pf .cd-pl{color:#C8485B}
            .cd-ps .cd-pl{color:#4A8A2A}
            .cd-pv{font-family:var(--bd);font-size:15px;font-weight:700}
            .cd-pf .cd-pv{color:#9A3040;text-decoration:line-through;text-decoration-thickness:1.5px}
            .cd-ps .cd-pv{color:#3A6A1A}
            .cd-pv small{font-size:9px;font-weight:500;opacity:.7}
            .cd-psz{font-family:var(--bd);font-size:8px;color:#A09080;margin-top:1px}

            .cd-btn{width:100%;padding:11px;border:none;border-radius:12px;background:var(--pk);color:white;font-family:var(--bd);font-size:11px;font-weight:700;letter-spacing:1.5px;text-transform:uppercase;cursor:pointer;transition:all .25s;display:flex;align-items:center;justify-content:center;gap:7px}
            .cd-btn:hover{background:#D12590;transform:scale(1.015);box-shadow:0 4px 12px rgba(233,30,140,0.25)}

            /* ── MODAL ── */
            .ov{position:fixed;inset:0;background:rgba(44,24,16,0.5);backdrop-filter:blur(10px);display:flex;align-items:center;justify-content:center;z-index:999;padding:16px;animation:fi .2s ease-out;margin-top: 120px;}
            .ml{background:var(--cr);border-radius:24px;max-width:540px;width:100%;box-shadow:0 40px 80px rgba(44,24,16,0.22);animation:su .3s cubic-bezier(.16,1,.3,1);max-height:92vh;overflow-y:auto}

            @media only screen and (max-width: 1024px) {
                .ov{
                    margin-top: 30px;
                }
                .ml {
                    max-height:73vh;
                }
            }

            .ml-top{position:relative;display:flex;gap:18px;padding:26px 24px 18px;align-items:flex-start}
            .ml-bot{flex-shrink:0;background:white;border-radius:18px;padding:14px;border:1px solid rgba(0,0,0,0.04);box-shadow:0 4px 14px rgba(44,24,16,0.04)}
            .ml-inf{flex:1;padding-top:4px}
            .ml-br{font-family:var(--bd);font-size:9px;font-weight:700;letter-spacing:2.5px;color:var(--pk);text-transform:uppercase}
            .ml-nm{font-size:24px;font-weight:600;color:var(--bk);line-height:1.15;margin:2px 0 3px}
            .ml-tp{font-family:var(--bd);font-size:12px;color:#A09080;margin-bottom:6px}
            .ml-rat{display:flex;align-items:center;gap:6px;margin-bottom:8px}
            .ml-rt{font-family:var(--bd);font-size:11px;color:#A09080}
            .ml-cls{position:absolute;top:16px;right:16px;width:34px;height:34px;border-radius:50%;border:1px solid rgba(0,0,0,0.06);background:white;display:flex;align-items:center;justify-content:center;cursor:pointer;color:#8B7B6B;font-size:16px;transition:all .2s}
            .ml-cls:hover{background:#F5F0EA;color:var(--bk)}

            /* Full Product Detail Section */
            .ml-detail{margin:0 24px 16px;padding:16px;background:white;border-radius:16px;border:1px solid rgba(0,0,0,0.04)}
            .ml-dtitle{font-family:var(--bd);font-size:9px;font-weight:700;letter-spacing:2px;text-transform:uppercase;color:#A09080;margin-bottom:10px}
            .ml-drow{display:flex;flex-wrap:wrap;gap:8px}
            .ml-dchip{display:inline-flex;align-items:center;gap:5px;padding:6px 12px;border-radius:9px;background:#FDFBF8;border:1px solid rgba(0,0,0,0.04);font-family:var(--bd);font-size:11px;color:#5A4A3A}
            .ml-dchip b{font-weight:700;color:var(--bk)}
            .ml-dprice{display:flex;align-items:baseline;gap:8px;margin-top:10px}
            .ml-dp-cur{font-family:var(--bd);font-size:22px;font-weight:700;color:var(--bk)}
            .ml-dp-cur small{font-size:12px;font-weight:500;color:#A09080}
            .ml-dp-old{font-family:var(--bd);font-size:13px;color:var(--pk);text-decoration:line-through;font-weight:500}
            .ml-dp-off{font-family:var(--bd);font-size:10px;background:rgba(233,30,140,0.08);color:var(--pk);padding:2px 8px;border-radius:6px;font-weight:700}

            .ml-div{height:1px;background:rgba(0,0,0,0.05);margin:0 24px}

            .ml-ch{padding:20px 24px 26px}
            .ml-cht{text-align:center;font-size:19px;font-weight:500;color:var(--bk);margin-bottom:16px;font-style:italic}

            /* CHOICES */
            .ch{border:2px solid rgba(0,0,0,0.06);border-radius:18px;padding:16px;margin-bottom:10px;cursor:pointer;transition:all .3s;display:flex;align-items:center;gap:14px;position:relative;overflow:hidden}
            .ch:hover{transform:scale(1.01)}
            .ch-s{background:linear-gradient(135deg,rgba(74,138,42,0.04),transparent)}
            .ch-s:hover{border-color:var(--sg);background:linear-gradient(135deg,rgba(74,138,42,0.08),rgba(74,138,42,0.02))}
            .ch-f:hover{border-color:var(--pk);background:rgba(233,30,140,0.02)}
            .ch-ic{width:48px;height:48px;border-radius:13px;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:20px}
            .ch-s .ch-ic{background:linear-gradient(135deg,#5A9A2A,#4A8A1A)}
            .ch-f .ch-ic{background:linear-gradient(135deg,var(--pk),#D12590)}
            .ch-dt{flex:1}
            .ch-lb{font-family:var(--bd);font-size:8px;font-weight:700;letter-spacing:1.8px;text-transform:uppercase;color:#A09080;margin-bottom:1px}
            .ch-hd{font-size:15px;font-weight:600;color:var(--bk);margin-bottom:2px}
            .ch-ds{font-family:var(--bd);font-size:11px;color:#A09080;line-height:1.4}
            .ch-ri{text-align:right;flex-shrink:0}
            .ch-pr{font-family:var(--bd);font-size:21px;font-weight:700;color:var(--bk)}
            .ch-pr small{font-size:10px;font-weight:400;color:#A09080}
            .ch-sz{font-family:var(--bd);font-size:10px;color:#A09080}
            .ch-pop{position:absolute;top:0;right:16px;background:linear-gradient(135deg,#5A9A2A,#4A8A1A);color:white;font-family:var(--bd);font-size:7.5px;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;padding:3px 11px 4px;border-radius:0 0 8px 8px}
            .ch-or{text-align:center;font-family:var(--bd);font-size:10px;color:#C0B0A0;margin:4px 0}

            .ch-fb{position:absolute;inset:0;background:rgba(253,251,248,0.96);display:flex;flex-direction:column;align-items:center;justify-content:center;border-radius:16px;animation:fi .2s ease-out}
            .ch-ck{width:46px;height:46px;border-radius:50%;background:linear-gradient(135deg,#5CB85C,#3D8B3D);display:flex;align-items:center;justify-content:center;margin-bottom:8px}
            .ch-ft{font-family:var(--bd);font-size:13px;font-weight:600;color:var(--bk)}

            .ts-empty{text-align:center;padding:56px 20px}
            .ts-empty i{font-size:42px;display:block;opacity:.3;margin-bottom:12px;font-style:normal}
            .ts-empty h3{font-size:20px;color:var(--bk);margin-bottom:5px}
            .ts-empty p{font-family:var(--bd);font-size:13px;color:#A09080}

            @keyframes rv{from{opacity:0;transform:translateY(-10px)}to{opacity:1;transform:translateY(0)}}
            @keyframes fi{from{opacity:0}to{opacity:1}}
            @keyframes su{from{opacity:0;transform:translateY(20px) scale(.97)}to{opacity:1;transform:translateY(0) scale(1)}}
            @keyframes ci{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:translateY(0)}}
        `}</style>

                {/* ════ HERO ════ */}
                <div className="hero">
                    <div className="h-pill">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L15 8.5L22 9.5L17 14.5L18 21.5L12 18L6 21.5L7 14.5L2 9.5L9 8.5Z" /></svg>
                        TRY SAMPLES
                    </div>
                    <h1 className="h-t">Try Before You <em>Commit</em></h1>
                    <p className="h-sub">
                        Why spend 37–100 KWD on a full bottle you might not love?
                        Try a small sample first — starting from just 4 KWD.
                    </p>

                    <div className="h-how">
                        <div className="h-step"><div className="h-sn">1</div>Browse & pick a scent</div>
                        <div className="h-step"><div className="h-sn">2</div>Get a 3–5ML sample</div>
                        <div className="h-step"><div className="h-sn">3</div>Love it? Buy full size</div>
                    </div>

                    <div className="h-bar">
                        <div className="h-sbox">
                            <svg className="h-si" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>
                            <input className="h-inp" placeholder="Search by name, brand, or note..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <div className="h-fl">
                            {["All", "Men", "Women", "Unisex"].map(f => (
                                <button key={f} className={`h-fb ${filter === f ? "on" : ""}`} onClick={() => setFilter(f)}>{f}</button>
                            ))}
                        </div>
                        <select className="h-sort" value={sort} onChange={e => setSort(e.target.value)}>
                            <option value="popular">Most Popular</option>
                            <option value="price-low">Sample: Low → High</option>
                            <option value="price-high">Sample: High → Low</option>
                            <option value="rating">Top Rated</option>
                        </select>
                    </div>
                </div>

                {/* ════ GRID ════ */}
                <div className="gw">
                    <div className="gc">
                        <span>{filtered.length} fragrance{filtered.length !== 1 ? "s" : ""} available to sample</span>
                    </div>

                    {filtered.length === 0 ? (
                        <div className="ts-empty">
                            <i>🔍</i>
                            <h3>No fragrances found</h3>
                            <p>Try a different name, brand, or note</p>
                        </div>
                    ) : (
                        <div className="gg">
                            {filtered.map((p, i) => (
                                <div key={p.id} className="cd" style={{ animation: ready ? `ci .35s ${i * .04}s ease-out both` : "none" }} onClick={() => open(p)}>
                                    <div className="cd-top">
                                        <div className="cd-badge">{p.sampleSize} SAMPLE</div>
                                        {p.discount > 0 && <div className="cd-sale">{p.discount}% OFF</div>}
                                        <img src={p.image} alt={p.name} style={{ height: 80, objectFit: "contain" }} />
                                    </div>
                                    <div className="cd-body">
                                        <div className="cd-brand">{p.brand}</div>
                                        <div className="cd-name">{p.name}</div>
                                        <div className="cd-type">{p.type} · {p.fullSize}</div>
                                        {p.rating > 0 && (
                                            <div className="cd-rat">
                                                <Stars rating={p.rating} size={11} />
                                                <span className="cd-rt">{p.rating} ({p.reviews})</span>
                                            </div>
                                        )}
                                        <div className="cd-notes">{p.notes.map(n => <span key={n} className="cd-note">{n}</span>)}</div>
                                        <div className="cd-stock">{p.sold} Sold · <b>{p.left} Left</b></div>

                                        {/* PRICE COMPARISON */}
                                        <div className="cd-prices">
                                            <div className="cd-pf">
                                                <div className="cd-pl">FULL BOTTLE</div>
                                                <div className="cd-pv">KWD {formatPrice(p.fullPrice)}</div>
                                                <div className="cd-psz">{p.fullSize}</div>
                                            </div>
                                            <div className="cd-ps">
                                                <div className="cd-pl">SAMPLE</div>
                                                <div className="cd-pv">KWD {formatPrice(p.samplePrice)}</div>
                                                <div className="cd-psz">{p.sampleSize}</div>
                                            </div>
                                        </div>

                                        <button className="cd-btn" onClick={e => { e.stopPropagation(); open(p); }}>
                                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M12 2L15 8.5L22 9.5L17 14.5L18 21.5L12 18L6 21.5L7 14.5L2 9.5L9 8.5Z" /></svg>
                                            TRY THIS SCENT
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* ════ MODAL ════ */}
                {modal && sel && (
                    <div className="ov" onClick={close}>
                        <div className="ml" onClick={e => e.stopPropagation()}>
                            {/* Top: Bottle + Name */}
                            <div className="ml-top">
                                <div className="ml-bot">
                                    <img src={sel.image} alt={sel.name} style={{ height: 80, objectFit: "contain" }} />
                                </div>
                                <div className="ml-inf">
                                    <div className="ml-br">{sel.brand}</div>
                                    <div className="ml-nm">{sel.name}</div>
                                    <div className="ml-tp">{sel.type} · {sel.fullSize}</div>
                                    {sel.rating > 0 && (
                                        <div className="ml-rat">
                                            <Stars rating={sel.rating} size={13} />
                                            <span className="ml-rt">{sel.rating} · {sel.reviews} ratings</span>
                                        </div>
                                    )}
                                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                                        {sel.notes.map(n => <span key={n} className="cd-note">{n}</span>)}
                                    </div>
                                </div>
                                <button className="ml-cls" onClick={close}>✕</button>
                            </div>

                            {/* Full Product Details */}
                            <div className="ml-detail">
                                <div className="ml-dtitle">FULL BOTTLE DETAILS</div>
                                <div className="ml-dprice">
                                    <div className="ml-dp-cur">KWD {formatPrice(sel.fullPrice)} <small>/ {sel.fullSize}</small></div>
                                    {sel.discount > 0 && (
                                        <>
                                            <div className="ml-dp-old">KWD {formatPrice(sel.originalFullPrice)}</div>
                                            <div className="ml-dp-off">{sel.discount}% OFF</div>
                                        </>
                                    )}
                                </div>
                                <div className="ml-drow" style={{ marginTop: 10 }}>
                                    <div className="ml-dchip">📦 <b>{sel.sold}</b> Sold</div>
                                    <div className="ml-dchip" style={{ color: sel.left <= 3 ? "#C8485B" : "#5A4A3A" }}>🔥 <b>{sel.left}</b> Left</div>
                                    <div className="ml-dchip">👤 {sel.gender}</div>
                                    <div className="ml-dchip">📐 Size: <b>{sel.fullSize}</b></div>
                                </div>
                            </div>

                            <div className="ml-div" />

                            {/* Choices */}
                            <div className="ml-ch">
                                <div className="ml-cht">Would you like to try or buy?</div>

                                {/* Sample */}
                                <div className="ch ch-s" onClick={() => add("sample")}>
                                    <div className="ch-pop">RECOMMENDED</div>
                                    <div className="ch-ic">💧</div>
                                    <div className="ch-dt">
                                        <div className="ch-lb">ORDER SAMPLE</div>
                                        <div className="ch-hd">{sel.sampleSize} Discovery Vial</div>
                                        <div className="ch-ds">Try it for {sel.sampleSize === "3ML" ? "5–7" : "7–10"} days before spending {formatPrice(sel.fullPrice)} KWD on the full bottle.</div>
                                    </div>
                                    <div className="ch-ri">
                                        <div className="ch-pr">{formatPrice(sel.samplePrice)} <small>KWD</small></div>
                                        <div className="ch-sz">/ {sel.sampleSize}</div>
                                    </div>
                                    {feedback === "sample" && (
                                        <div className="ch-fb">
                                            <div className="ch-ck"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg></div>
                                            <div className="ch-ft">Sample added to bag!</div>
                                        </div>
                                    )}
                                </div>

                                <div className="ch-or">— or —</div>

                                {/* Full bottle */}
                                <div className="ch ch-f" onClick={() => add("full")}>
                                    <div className="ch-ic">🧴</div>
                                    <div className="ch-dt">
                                        <div className="ch-lb">BUY FULL BOTTLE</div>
                                        <div className="ch-hd">{sel.fullSize} Full Size</div>
                                        <div className="ch-ds">
                                            Already love it? Get the full bottle
                                            {sel.discount > 0 ? ` at ${sel.discount}% off — KWD ${formatPrice(sel.fullPrice)} instead of ${formatPrice(sel.originalFullPrice)}.` : ` for KWD ${formatPrice(sel.fullPrice)}.`}
                                        </div>
                                    </div>
                                    <div className="ch-ri">
                                        <div className="ch-pr">{formatPrice(sel.fullPrice)} <small>KWD</small></div>
                                        <div className="ch-sz">/ {sel.fullSize}</div>
                                    </div>
                                    {feedback === "full" && (
                                        <div className="ch-fb">
                                            <div className="ch-ck"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg></div>
                                            <div className="ch-ft">Full bottle added to bag!</div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            <Footer />
        </>
    );
}
